import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Save, Wrench } from "lucide-react";
import { FieldLabel } from "@/pages/admin/FieldLabel";

const DAY_LABELS_FULL = { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" };
const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DEFAULT_HOURS = DAY_ORDER.map((d) => ({ day: d, open: "08:00", close: "17:00", closed: false }));

export default function SiteManager() {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [form, setForm] = React.useState({
    about_heading: "",
    about_paragraph: "",
    contact_email: "",
    contact_phone: "",
    address_line1: "",
    address_line2: "",
    opening_hours: DEFAULT_HOURS,
    review_delay_hours: 24,
  });

  React.useEffect(() => {
    api.get("/settings").then((r) => {
      const s = r.data || {};
      const hoursMap = Object.fromEntries((s.opening_hours || []).map((h) => [h.day, h]));
      const orderedHours = DAY_ORDER.map((d) => hoursMap[d] || { day: d, open: "08:00", close: "17:00", closed: false });
      setForm({
        about_heading: s.about_heading || "",
        about_paragraph: s.about_paragraph || "",
        contact_email: s.contact_email || "",
        contact_phone: s.contact_phone || "",
        address_line1: s.address_line1 || "",
        address_line2: s.address_line2 || "",
        opening_hours: orderedHours,
        review_delay_hours: s.review_delay_hours ?? 24,
      });
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const updateHour = (day, patch) => {
    setForm((f) => ({
      ...f,
      opening_hours: f.opening_hours.map((h) => (h.day === day ? { ...h, ...patch } : h)),
    }));
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await api.patch("/admin/settings", { ...form, review_delay_hours: Number(form.review_delay_hours) });
      toast.success("Site settings updated — live on the public site");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest py-10">Loading site settings…</div>;
  }

  return (
    <form data-testid="site-form" onSubmit={save} className="space-y-10">
      <div className="flex items-start gap-3 border-l-2 border-brand-red pl-4 mb-2">
        <Wrench className="h-5 w-5 text-brand-red mt-0.5" />
        <div>
          <h3 className="font-display uppercase text-xl text-white tracking-tight">Site content</h3>
          <p className="text-sm text-zinc-400 max-w-xl mt-1">
            Edit the About section, contact details, address and opening hours. Changes appear instantly on the public site after you save.
          </p>
        </div>
      </div>

      <section>
        <h4 className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red mb-4">About section</h4>
        <div className="space-y-4">
          <FieldLabel label="Heading">
            <input
              data-testid="site-about-heading"
              className="ta-svc-input"
              value={form.about_heading}
              onChange={(e) => setForm({ ...form, about_heading: e.target.value })}
              maxLength={120}
              placeholder="Built by mechanics, run like a workshop should be."
            />
          </FieldLabel>
          <FieldLabel label="Paragraph (max 2000 chars)">
            <textarea
              data-testid="site-about-paragraph"
              className="ta-svc-input min-h-[140px]"
              value={form.about_paragraph}
              onChange={(e) => setForm({ ...form, about_paragraph: e.target.value })}
              maxLength={2000}
              placeholder="Tell visitors who you are…"
            />
          </FieldLabel>
        </div>
      </section>

      <section>
        <h4 className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red mb-4">Contact</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FieldLabel label="Public email">
            <input
              data-testid="site-contact-email"
              type="email"
              className="ta-svc-input"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="hello@tassieautomotive.com.au"
            />
          </FieldLabel>
          <FieldLabel label="Public phone">
            <input
              data-testid="site-contact-phone"
              className="ta-svc-input"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              placeholder="(03) 6288 7861"
            />
          </FieldLabel>
          <FieldLabel label="Address line 1">
            <input
              data-testid="site-address-1"
              className="ta-svc-input"
              value={form.address_line1}
              onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
              placeholder="46 Grove Rd"
            />
          </FieldLabel>
          <FieldLabel label="Address line 2">
            <input
              data-testid="site-address-2"
              className="ta-svc-input"
              value={form.address_line2}
              onChange={(e) => setForm({ ...form, address_line2: e.target.value })}
              placeholder="Glenorchy TAS 7010"
            />
          </FieldLabel>
        </div>
      </section>

      <section>
        <h4 className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red mb-4">Review requests</h4>
        <div className="border border-zinc-900 bg-zinc-950 p-4 max-w-md">
          <FieldLabel label="Send Google review request">
            <select
              data-testid="site-review-delay"
              className="ta-svc-input"
              value={form.review_delay_hours}
              onChange={(e) => setForm({ ...form, review_delay_hours: Number(e.target.value) })}
            >
              <option value={0}>Immediately after job completed</option>
              <option value={1}>1 hour after completion</option>
              <option value={4}>4 hours after completion</option>
              <option value={24}>24 hours after completion</option>
              <option value={48}>48 hours after completion</option>
            </select>
          </FieldLabel>
          <p className="mt-2 text-[11px] text-zinc-600">
            Sent by both SMS and email with your Google review link.
          </p>
        </div>
      </section>

      <section>
        <h4 className="font-mono text-[10px] uppercase tracking-[0.4em] text-brand-red mb-4">Opening hours</h4>
        <div className="border border-zinc-900 bg-zinc-950 divide-y divide-zinc-900">
          {form.opening_hours.map((h) => (
            <div key={h.day} data-testid={`site-hours-${h.day}`} className="grid grid-cols-12 gap-3 items-center px-4 py-3">
              <div className="col-span-3 sm:col-span-2 font-mono text-[11px] uppercase tracking-widest text-zinc-300">
                {DAY_LABELS_FULL[h.day]}
              </div>
              <label className="col-span-3 sm:col-span-2 inline-flex items-center gap-2 text-zinc-400 text-xs font-mono uppercase tracking-widest cursor-pointer">
                <input
                  data-testid={`site-hours-${h.day}-closed`}
                  type="checkbox"
                  checked={h.closed}
                  onChange={(e) => updateHour(h.day, { closed: e.target.checked })}
                  className="accent-brand-red"
                />
                Closed
              </label>
              <div className="col-span-3 sm:col-span-4">
                <input
                  data-testid={`site-hours-${h.day}-open`}
                  type="time"
                  className="ta-svc-input disabled:opacity-40"
                  value={h.open || ""}
                  disabled={h.closed}
                  onChange={(e) => updateHour(h.day, { open: e.target.value })}
                />
              </div>
              <div className="col-span-3 sm:col-span-4">
                <input
                  data-testid={`site-hours-${h.day}-close`}
                  type="time"
                  className="ta-svc-input disabled:opacity-40"
                  value={h.close || ""}
                  disabled={h.closed}
                  onChange={(e) => updateHour(h.day, { close: e.target.value })}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="flex items-center gap-3">
        <button
          data-testid="site-save"
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 px-7 py-3 bg-brand-red text-white text-xs font-mono uppercase tracking-widest hover:bg-[#B91C2B] disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save site settings"}
        </button>
        <span className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest">
          Changes are live immediately
        </span>
      </div>
    </form>
  );
}

// ---------- Week calendar view of confirmed bookings ----------
